import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { createCRMContact } from '../../crm/api';
import { parseApiError } from '@/common/utils';
import { useBodyScrollLock } from '@/common/hooks';

function FieldLabel({ children, htmlFor, required }) {
  return (
    <label
      htmlFor={htmlFor}
      className="text-[13px] font-medium text-text-secondary leading-none"
    >
      {children}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );
}

/**
 * CreateContactModal
 *
 * A small floating form that appears on top of (but does NOT close) the
 * Edit Appointment modal.  Rendered via a portal so it escapes any
 * overflow:hidden ancestors.
 *
 * Props:
 *  - isOpen      {boolean}
 *  - onClose     {() => void}
 *  - companyId   {string|number}
 *  - initialName {string}  — pre-fill from the search query
 *  - onCreated   {(contact: {id, name, contact}) => void}
 */
export function CreateContactModal({
  isOpen,
  onClose,
  companyId,
  initialName = '',
  onCreated,
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const nameRef = useRef(null);

  useBodyScrollLock(isOpen);

  // Reset & pre-fill whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      setName(initialName || '');
      setPhone('');
      setErrors({});
      setIsSubmitting(false);
      // Focus name field on next tick
      setTimeout(() => nameRef.current?.focus(), 80);
    }
  }, [isOpen, initialName]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = 'Name is required.';
    if (!phone.trim()) errs.phone = 'Phone is required.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (isSubmitting) return;

    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setIsSubmitting(true);
    try {
      const newContact = await createCRMContact({
        companyId,
        payload: {
          name: name.trim(),
          phone: phone.trim(),
          consent_status: 'unknown',
        },
      });

      toast.success('Contact created successfully.');
      onCreated?.({
        id: newContact.id,
        name:
          newContact.name ||
          [newContact.first_name, newContact.last_name]
            .filter(Boolean)
            .join(' ') ||
          name.trim(),
        contact: newContact,
      });
    } catch (err) {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to create contact.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop — clicking directly on it closes the mini-modal only */}
      <div
        className="fixed inset-0 z-[199998] bg-black/20"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      />

      {/* Mini-modal panel */}
      <div
        data-modal-root
        role="dialog"
        aria-modal="true"
        aria-label="Create New Contact"
        className="fixed z-[199999] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
                   w-full max-w-[360px] bg-white rounded-2xl shadow-2xl border border-[#EBF4EA]
                   flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EBEBEB]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#EBF4EA] flex items-center justify-center flex-shrink-0">
              <svg
                className="w-4 h-4 text-[#50AB3D]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M12 5v14M5 12h14"
                />
              </svg>
            </div>
            <h2 className="text-[15px] font-semibold text-[#1F2241]">
              Create New Contact
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close create contact form"
            className="w-7 h-7 flex items-center justify-center rounded-lg text-[#868686]
                       hover:bg-[#F5F5F5] hover:text-[#1F2241] transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form body */}
        <form
          onSubmit={handleSubmit}
          autoComplete="off"
          className="flex flex-col gap-4 p-5"
        >
          {/* Name field */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="cc-name" required>
              Name
            </FieldLabel>
            <input
              ref={nameRef}
              id="cc-name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name)
                  setErrors((prev) => ({ ...prev, name: undefined }));
              }}
              disabled={isSubmitting}
              placeholder="Enter contact name"
              className={`h-11 w-full px-3 rounded-xl border text-sm font-medium text-[#1F2241]
                          bg-white focus:outline-none transition-colors
                          placeholder:text-[#B0B0B0] disabled:opacity-60 disabled:cursor-not-allowed
                          ${
                            errors.name
                              ? 'border-red-400 focus:border-red-400 ring-2 ring-red-400/15'
                              : 'border-[#EBF4EA] focus:border-[#50AB3D] focus:ring-2 focus:ring-[#50AB3D]/15'
                          }`}
            />
            {errors.name && (
              <p className="text-xs text-red-500 font-medium">{errors.name}</p>
            )}
          </div>

          {/* Phone field */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="cc-phone" required>
              Phone
            </FieldLabel>
            <input
              id="cc-phone"
              type="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                if (errors.phone)
                  setErrors((prev) => ({ ...prev, phone: undefined }));
              }}
              disabled={isSubmitting}
              placeholder="Enter phone number"
              className={`h-11 w-full px-3 rounded-xl border text-sm font-medium text-[#1F2241]
                          bg-white focus:outline-none transition-colors
                          placeholder:text-[#B0B0B0] disabled:opacity-60 disabled:cursor-not-allowed
                          ${
                            errors.phone
                              ? 'border-red-400 focus:border-red-400 ring-2 ring-red-400/15'
                              : 'border-[#EBF4EA] focus:border-[#50AB3D] focus:ring-2 focus:ring-[#50AB3D]/15'
                          }`}
            />
            {errors.phone && (
              <p className="text-xs text-red-500 font-medium">{errors.phone}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2.5 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 h-10 px-4 rounded-xl border border-[#EBEBEB] text-sm font-medium
                         text-[#868686] hover:bg-[#F5F5F5] hover:text-[#1F2241] transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 h-10 px-4 rounded-xl bg-[#50AB3D] text-white text-sm font-medium
                         hover:bg-[#439E31] active:bg-[#3A8E2A] transition-colors
                         disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="w-3.5 h-3.5 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  Creating...
                </>
              ) : (
                <>
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="M12 5v14M5 12h14"
                    />
                  </svg>
                  Create Contact
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
